import { useCallback, useMemo, useState } from 'react';
import { observer } from 'mobx-react';
import upperFirst from 'lodash/upperFirst';

import Button, { PRIMARY } from '@/common/components/Button/Button';
import FancyDropdownButton from '@/common/components/FancyDropdownButton/FancyDropdownButton';
import Icon from '@/common/components/Icon/Icon';
import { SIZE_MEDIUM } from '@/common/components/Label/Label';
import { MenuProvider } from '@/common/components/MenuList/MenuContext/MenuContext';
import MenuItem from '@/common/components/MenuList/MenuItem/MenuItem';
import MenuList from '@/common/components/MenuList/MenuList';
import { DriveItemType } from '@/drive/entities/DriveItem/DriveItem';
import { useDriveModalTreeStore } from '@/forwarding/components/ForwardModal/ForwardModalBody/DriveModalTree/DriveModalTreeStore';
import { useRecentDestinationsStore } from '@/forwarding/components/ForwardModal/ForwardModalBody/RecentDestinations/RecentDestinationsStore';
import { ForwardModalTab } from '@/forwarding/components/ForwardModal/ForwardModalTab';

import styles from './ForwardModalFooter.module.css';

export interface ForwardModalFooterProps {
  onClose: () => void;
  onSave: () => void;
  activeTab: ForwardModalTab;
  isSaveShown?: boolean;
  isSubmitting?: boolean;
}

function ForwardModalFooter(props: ForwardModalFooterProps) {
  const { onClose, onSave, activeTab, isSaveShown = true, isSubmitting = false } = props;
  const { productType, isPendingCreation, selected, startCreation } = useDriveModalTreeStore();
  const { selected: selectedDestination } = useRecentDestinationsStore();
  const [isOpened, setIsOpen] = useState(false);
  const handleCloseMenu = useCallback(() => setIsOpen(false), []);

  const isSelected = useMemo(() => {
    if (activeTab === ForwardModalTab.DriveTree) {
      return !!selected;
    }

    if (activeTab === ForwardModalTab.Recent) {
      return !!selectedDestination;
    }

    return false;
  }, [activeTab, selected, selectedDestination]);

  return (
    <div className={styles.forwardModalFooter}>
      <div>
        {activeTab === ForwardModalTab.DriveTree && productType && (
          <MenuProvider onClick={handleCloseMenu}>
            <FancyDropdownButton
              className={styles.button}
              aria-label="New"
              label="New"
              isOpen={isOpened}
              onChangeState={setIsOpen}
              loading={isPendingCreation}
            >
              <MenuList>
                <MenuItem aria-label="New folder" onClick={() => startCreation(DriveItemType.Folder)}>
                  <Icon name="folder" />
                  Folder
                </MenuItem>

                {productType && (
                  <MenuItem aria-label="New collection" onClick={() => startCreation(DriveItemType.Collection)}>
                    <Icon name="like" />

                    {`${upperFirst(productType)} Collection`}
                  </MenuItem>
                )}
              </MenuList>
            </FancyDropdownButton>
          </MenuProvider>
        )}

        {activeTab === ForwardModalTab.DriveTree && !productType && (
          <Button aria-label="New Folder" onClick={() => startCreation(DriveItemType.Folder)} loading={isPendingCreation}>
            New Folder
          </Button>
        )}
      </div>

      <div className={styles.rightGroup}>
        <Button
          aria-label="drive-item-cancel"
          className={styles.button}
          type="button"
          disabled={isSubmitting}
          onClick={onClose}
        >
          Cancel
        </Button>

        {isSaveShown && (
          <Button
            className={styles.button}
            aria-label="drive-item-save"
            type="button"
            color={PRIMARY}
            size={SIZE_MEDIUM}
            disabled={!isSelected || isSubmitting}
            loading={isSubmitting}
            onClick={onSave}
          >
            Save
          </Button>
        )}
      </div>
    </div>
  );
}

export default observer(ForwardModalFooter);
